// NerdMath core logic — pure functions, no React.
// Every exported function here is covered by tests/mathtest.mjs
// against known textbook values. Verify-before-ship discipline.

// ---------- Quadratic solver ----------
// Solves ax^2 + bx + c = 0. Returns {type, roots, steps[]}
export function solveQuadratic(a, b, c) {
  const steps = [];
  if (a === 0) {
    // Degenerates to linear bx + c = 0
    if (b === 0) {
      return { type: c === 0 ? "infinite" : "none", roots: [], steps: ["a = 0 and b = 0 — not a valid equation"] };
    }
    const x = -c / b;
    return {
      type: "linear",
      roots: [x],
      steps: [
        `a = 0, so this is linear: ${b}x + ${c} = 0`,
        `x = -c / b = ${-c} / ${b} = ${round(x, 6)}`,
      ],
    };
  }
  const disc = b * b - 4 * a * c;
  steps.push(`Discriminant: b² − 4ac = (${b})² − 4(${a})(${c}) = ${round(disc, 6)}`);
  if (disc > 0) {
    const sq = Math.sqrt(disc);
    const r1 = (-b + sq) / (2 * a);
    const r2 = (-b - sq) / (2 * a);
    steps.push(`Discriminant > 0 → two real roots`);
    steps.push(`x = (−b ± √Δ) / 2a = (${-b} ± ${round(sq, 6)}) / ${2 * a}`);
    steps.push(`x₁ = ${round(r1, 6)},  x₂ = ${round(r2, 6)}`);
    return { type: "two-real", roots: [r1, r2], steps };
  } else if (disc === 0) {
    const r = -b / (2 * a);
    steps.push(`Discriminant = 0 → one repeated real root`);
    steps.push(`x = −b / 2a = ${-b} / ${2 * a} = ${round(r, 6)}`);
    return { type: "one-real", roots: [r], steps };
  } else {
    const re = -b / (2 * a);
    const im = Math.sqrt(-disc) / (2 * a);
    steps.push(`Discriminant < 0 → two complex conjugate roots`);
    steps.push(`x = ${round(re, 6)} ± ${round(Math.abs(im), 6)}i`);
    return {
      type: "complex",
      roots: [
        { re, im },
        { re, im: -im },
      ],
      steps,
    };
  }
}

// ---------- Logarithm ----------
// log_base(value). base defaults to e if base == null.
export function logarithm(value, base) {
  if (value <= 0) return { ok: false, error: "Logarithm undefined for values ≤ 0" };
  if (base == null) {
    const result = Math.log(value);
    const r = (x) => Math.round(x * 1e6) / 1e6;
    return { ok: true, result, label: `ln(${value})`, steps: [`ln(${value}) = ${r(result)}`] };
  }
  if (base <= 0 || base === 1) return { ok: false, error: "Base must be > 0 and ≠ 1" };
  const lnV = Math.log(value), lnB = Math.log(base);
  const result = lnV / lnB;
  const r = (x) => Math.round(x * 1e6) / 1e6;
  const steps = [
    `log_${base}(${value}) = ln(${value}) / ln(${base})`,
    `= ${r(lnV)} / ${r(lnB)}`,
    `= ${r(result)}`,
  ];
  return { ok: true, result, label: `log_${base}(${value})`, steps };
}

// ---------- Trig reference ----------
// Returns exact-ish values for the unit circle at a given degree.
export const EXACT = {
  0: { sin: "0", cos: "1", tan: "0" },
  30: { sin: "1/2", cos: "√3/2", tan: "√3/3" },
  45: { sin: "√2/2", cos: "√2/2", tan: "1" },
  60: { sin: "√3/2", cos: "1/2", tan: "√3" },
  90: { sin: "1", cos: "0", tan: "undefined" },
  120: { sin: "√3/2", cos: "-1/2", tan: "-√3" },
  135: { sin: "√2/2", cos: "-√2/2", tan: "-1" },
  150: { sin: "1/2", cos: "-√3/2", tan: "-√3/3" },
  180: { sin: "0", cos: "-1", tan: "0" },
  210: { sin: "-1/2", cos: "-√3/2", tan: "√3/3" },
  225: { sin: "-√2/2", cos: "-√2/2", tan: "1" },
  240: { sin: "-√3/2", cos: "-1/2", tan: "√3" },
  270: { sin: "-1", cos: "0", tan: "undefined" },
  300: { sin: "-√3/2", cos: "1/2", tan: "-√3" },
  315: { sin: "-√2/2", cos: "√2/2", tan: "-1" },
  330: { sin: "-1/2", cos: "√3/2", tan: "-√3/3" },
  360: { sin: "0", cos: "1", tan: "0" },
};
export function trigExact(deg) {
  return EXACT[((deg % 360) + 360) % 360] || null;
}
export function trigNumeric(deg) {
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  return {
    rad,
    sin: Math.sin(rad),
    cos,
    tan: Math.abs(cos) < 1e-12 ? null : Math.tan(rad),
  };
}

// ---------- Matrix ----------
export function determinant(m) {
  const n = m.length;
  if (n === 1) return m[0][0];
  if (n === 2) return m[0][0] * m[1][1] - m[0][1] * m[1][0];
  // Laplace expansion along first row
  let det = 0;
  for (let j = 0; j < n; j++) {
    const minor = m.slice(1).map((row) => row.filter((_, k) => k !== j));
    det += (j % 2 === 0 ? 1 : -1) * m[0][j] * determinant(minor);
  }
  return det;
}

export function inverse(m) {
  // Shape validation: must be a non-empty square matrix of finite numbers.
  if (!Array.isArray(m) || m.length === 0) return { ok: false, error: "Matrix is empty" };
  const n = m.length;
  if (!m.every((row) => Array.isArray(row) && row.length === n && row.every(Number.isFinite)))
    return { ok: false, error: "Matrix must be square with finite numeric entries" };
  const det = determinant(m);
  // The determinant precheck is a fast reject, but float cancellation on
  // large integer matrices can make an exactly-singular matrix show a huge
  // nonzero det. So we do NOT trust it alone — the real singularity guard
  // is the zero-pivot check inside the elimination below.
  const a = m.map((row, i) => [...row, ...Array.from({ length: n }, (_, j) => (i === j ? 1 : 0))]);
  // Scale-aware tolerance for pivot detection.
  let scale = 0;
  for (const row of m) for (const v of row) scale = Math.max(scale, Math.abs(v));
  const eps = (scale || 1) * 1e-9;
  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let r = col + 1; r < n; r++) if (Math.abs(a[r][col]) > Math.abs(a[pivot][col])) pivot = r;
    [a[col], a[pivot]] = [a[pivot], a[col]];
    const pv = a[col][col];
    if (Math.abs(pv) < eps)
      return { ok: false, error: "Matrix is singular (no inverse)" };
    for (let k = 0; k < 2 * n; k++) a[col][k] /= pv;
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const factor = a[r][col];
      for (let k = 0; k < 2 * n; k++) a[r][k] -= factor * a[col][k];
    }
  }
  return { ok: true, inverse: a.map((row) => row.slice(n).map((v) => round(v, 10))), determinant: det };
}

// Solve Ax = b via inverse (small systems only — fine for a study tool)
export function solveLinearSystem(A, b) {
  if (!Array.isArray(A) || !Array.isArray(b))
    return { ok: false, error: "Provide a matrix A and vector b" };
  if (b.length !== A.length)
    return { ok: false, error: "b length must equal the number of rows in A" };
  if (!b.every(Number.isFinite))
    return { ok: false, error: "Vector b must contain finite numbers" };
  const inv = inverse(A);
  if (!inv.ok) return { ok: false, error: inv.error };
  const x = inv.inverse.map((row) => round(row.reduce((s, v, j) => s + v * b[j], 0), 8));
  return { ok: true, solution: x };
}

// ---------- Scientific expression evaluator ----------
// Safe shunting-yard parser. Supports + - * / ^, unary minus, parens,
// constants pi/e, functions sin cos tan asin acos atan ln log sqrt abs
// exp, and factorial via "!". Trig in radians.
const FUNCS = {
  sin: Math.sin, cos: Math.cos, tan: Math.tan,
  asin: Math.asin, acos: Math.acos, atan: Math.atan,
  ln: Math.log, log: (x) => Math.log10(x), sqrt: Math.sqrt,
  abs: Math.abs, exp: Math.exp,
};
function factorial(n) {
  if (n < 0 || !Number.isInteger(n)) return NaN;
  let r = 1;
  for (let i = 2; i <= n; i++) r *= i;
  return r;
}
export function evaluate(expr) {
  try {
    const tokens = tokenize(expr);
    const rpn = toRPN(tokens);
    const val = evalRPN(rpn);
    if (!isFinite(val)) return { ok: false, error: "Result is not finite" };
    return { ok: true, result: val };
  } catch (e) {
    return { ok: false, error: e.message || "Invalid expression" };
  }
}
function tokenize(s) {
  const out = [];
  let i = 0;
  // Whitespace is a TOKEN BOUNDARY, not deleted. Deleting it made
  // "2 2" parse as 22 — a wrong answer a student cannot catch. The
  // number scanner below stops at whitespace, so two space-separated
  // numbers become two adjacent number tokens with no operator, which
  // toRPN rejects as malformed. Spaces around operators/parens/names
  // are harmless (those are single tokens) so "1 + 2" and "sin (2)"
  // stay valid.
  while (i < s.length) {
    const c = s[i];
    if (/\s/.test(c)) { i++; continue; }
    if (/[0-9.]/.test(c)) {
      let num = "";
      while (i < s.length && /[0-9.]/.test(s[i])) num += s[i++];
      // A valid number has at most one '.' and at least one digit.
      // parseFloat("1.2.3") silently returns 1.2 — reject instead.
      if ((num.match(/\./g) || []).length > 1 || !/\d/.test(num))
        throw new Error(`Invalid number: ${num}`);
      out.push({ t: "num", v: parseFloat(num) });
      // After a number, the next non-space must NOT be another number
      // (that means whitespace-separated operands like "2 2" / "5 5").
      let j = i;
      while (j < s.length && /\s/.test(s[j])) j++;
      if (j > i && j < s.length && /[0-9.]/.test(s[j]))
        throw new Error("Missing operator between values");
      continue;
    }
    if (/[a-z]/i.test(c)) {
      let name = "";
      while (i < s.length && /[a-z]/i.test(s[i])) name += s[i++];
      if (name === "pi") out.push({ t: "num", v: Math.PI });
      else if (name === "e") out.push({ t: "num", v: Math.E });
      else if (FUNCS[name]) out.push({ t: "func", v: name });
      else throw new Error(`Unknown name: ${name}`);
      continue;
    }
    if ("+-*/^()!".includes(c)) {
      out.push({ t: "op", v: c });
      i++;
      continue;
    }
    throw new Error(`Unexpected character: ${c}`);
  }
  return out;
}
function toRPN(tokens) {
  const out = [];
  const ops = [];
  // Two distinct unary minuses:
  //  - "u-"  : leading/grouping unary minus. Binds LOOSER than ^ so
  //            -3^2 = -9 (math convention).
  //  - "u^-" : unary minus that is the start of an exponent's RHS
  //            (the "-" in 2^-1). It negates the ENTIRE exponent
  //            operand, which may itself be an exponent chain:
  //              2^-1     = 0.5
  //              2^-3^2   = 2^-(3^2)   = 2^-9
  //              -2^-2    = -(2^-2)    = -0.25
  //            Mechanism: u^- has precedence above ^ (so 2^-1 binds),
  //            but a ^ that ARRIVES while u^- is on the op stack must
  //            NOT pop it — the exponent chain to the right of the
  //            minus must build first, then get negated. That single
  //            structural exception (rather than a precedence number)
  //            is what makes every case correct simultaneously.
  const prec = { "+": 2, "-": 2, "*": 3, "/": 3, "u-": 3.5, "^": 4, "u^-": 5, "!": 6 };
  const rightAssoc = { "^": true, "u-": true, "u^-": true };
  let prev = null;
  for (const tk of tokens) {
    if (tk.t === "num") out.push(tk);
    else if (tk.t === "func") ops.push(tk);
    else if (tk.v === "(") ops.push(tk);
    else if (tk.v === ")") {
      while (ops.length && ops[ops.length - 1].v !== "(") out.push(ops.pop());
      if (!ops.length) throw new Error("Mismatched parentheses");
      ops.pop();
      if (ops.length && ops[ops.length - 1].t === "func") out.push(ops.pop());
    } else {
      // operator
      let op = tk.v;
      const isUnary = op === "-" && (prev === null || (prev.t === "op" && prev.v !== ")" && prev.v !== "!"));
      // unary minus that directly follows ^ is exponent-negation (u^-)
      if (isUnary) op = (prev && prev.t === "op" && prev.v === "^") ? "u^-" : "u-";
      if (op === "!") {
        out.push(tk);
      } else {
        while (
          ops.length &&
          ops[ops.length - 1].t === "op" &&
          ops[ops.length - 1].v !== "(" &&
          // Structural exception: a ^ never pops a u^- — the exponent
          // chain to the right of the minus must build before negating.
          !(op === "^" && ops[ops.length - 1].v === "u^-") &&
          (prec[ops[ops.length - 1].v] > prec[op] ||
            (prec[ops[ops.length - 1].v] === prec[op] && !rightAssoc[op]))
        )
          out.push(ops.pop());
        ops.push({ t: "op", v: op });
      }
    }
    prev = tk;
  }
  while (ops.length) {
    const o = ops.pop();
    if (o.v === "(") throw new Error("Mismatched parentheses");
    out.push(o);
  }
  return out;
}
function evalRPN(rpn) {
  const st = [];
  for (const tk of rpn) {
    if (tk.t === "num") st.push(tk.v);
    else if (tk.t === "func") st.push(FUNCS[tk.v](st.pop()));
    else {
      const op = tk.v;
      if (op === "u-" || op === "u^-") st.push(-st.pop());
      else if (op === "!") st.push(factorial(st.pop()));
      else {
        const b = st.pop();
        const a = st.pop();
        if (op === "+") st.push(a + b);
        else if (op === "-") st.push(a - b);
        else if (op === "*") st.push(a * b);
        else if (op === "/") st.push(a / b);
        else if (op === "^") st.push(Math.pow(a, b));
      }
    }
  }
  if (st.length !== 1) throw new Error("Invalid expression");
  return st[0];
}

// ---------- shared ----------
export function round(x, p = 6) {
  if (typeof x !== "number" || !isFinite(x)) return x;
  const f = Math.pow(10, p);
  return Math.round(x * f) / f;
}

